import { Alert } from '@mui/material';
import { AxiosError } from 'axios';
import { FormikHelpers, FormikProps, Formik } from 'formik';
import { filter, groupBy, has, isEmpty, map } from 'lodash';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { internalWalletsApi } from 'api';
import {
  CloseFormikDialogResult,
  Dialog,
  DialogProps,
  FormControls,
  FormikCheckbox,
  FormikSelect,
} from 'components';
import { QueryKey } from 'enums';
import { useMutation, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { InternalWallet } from 'types';
import { formUtils, validationUtils, walletsUtils } from 'utils';

type Values = Pick<InternalWallet, 'p2pProviderId'> & {
  showProvidersWithoutWalletsOnly?: boolean;
};

type Props = DialogProps<InternalWallet>;

export const P2PProviderWalletDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.internal_wallets.p2p_provider_wallet_dialog',
  });

  const { t: tCommon } = useTranslation();

  const { p2pProviders } = useUserContext();

  const queryClient = useQueryClient();

  const initialValues: Values = useMemo(
    () => ({
      p2pProviderId: '',
      showProvidersWithoutWalletsOnly: false,
    }),
    [],
  );

  const mutationCreate = useMutation<InternalWallet, AxiosError, string>(
    internalWalletsApi.createP2PProvider,
    {
      notifierMessages: {
        error: walletsUtils.getErrorMessage,
      },
    },
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      const options = {
        onSuccess: () => {
          queryClient.invalidateQueries(
            QueryKey.InternalP2PProviderWalletsPaginated,
          );
          formikHelpers.resetForm();
          onClose({ ok: true });
        },
        onError: (error: AxiosError) =>
          formikHelpers.setErrors(validationUtils.getFormErrors(error)),
        onSettled: () => {
          formikHelpers.setSubmitting(false);
        },
      };

      formikHelpers.setSubmitting(true);

      mutationCreate.mutate(values.p2pProviderId!, options);
    },
    [mutationCreate, queryClient, onClose],
  );

  const queryResultProvidersWallets = useQuery(
    QueryKey.InternalP2PProviderWallets,
    internalWalletsApi.getAllOfP2PProviders,
    { enabled: open },
  );

  const providersWithWallets = useMemo(
    () =>
      groupBy(
        queryResultProvidersWallets?.data,
        (wallet) => wallet.p2pProviderId,
      ),
    [queryResultProvidersWallets?.data],
  );

  const getP2PProvidersOptions = useCallback(
    (formik: FormikProps<Values>) => {
      const options = formUtils.getOptions(p2pProviders || []);
      if (formik.values.showProvidersWithoutWalletsOnly) {
        return filter(
          options,
          ({ value }) => !has(providersWithWallets, value),
        );
      }
      return options;
    },
    [p2pProviders, providersWithWallets],
  );

  const resetProviderSelection = useCallback((formik: FormikProps<Values>) => {
    formik.setFieldValue('p2pProviderId', '');
  }, []);

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok || !result.data) {
        result.data?.formikHelpers?.resetForm();
        onClose(result);
      } else {
        handleSubmit(result.data?.values, result.data?.formikHelpers);
      }
    },
    [handleSubmit, onClose],
  );

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        p2pProviderId: Yup.string().required(tCommon('errors.required')),
        showProvidersWithoutWalletsOnly: Yup.boolean().optional(),
      }),
    [tCommon],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
      isInitialValid={false}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <Dialog
          open={open}
          title={t('title')}
          data={{ values: formik.values, formikHelpers: formik }}
          mutation={mutationCreate}
          disabled={formik.isSubmitting}
          okDisabled={!formik.isValid}
          onClose={handleClose}
        >
          <FormControls sx={{ marginBottom: '2rem' }}>
            <FormikCheckbox
              name="showProvidersWithoutWalletsOnly"
              label={t('fields.show_providers_without_wallets_only')}
              onChange={() => resetProviderSelection(formik)}
            />
            <FormikSelect
              name="p2pProviderId"
              label={t('fields.p2p_provider')}
              required
              options={getP2PProvidersOptions(formik)}
              key={String(formik.values.showProvidersWithoutWalletsOnly)} // to reset autocomplete text input value
              disablePortal={false}
            />
            {formik.values.p2pProviderId &&
              !isEmpty(providersWithWallets[formik.values.p2pProviderId]) && (
                <Alert severity="warning">
                  <span>{`${t('provider_wallets')}:`}</span>
                  <ul>
                    {map(
                      providersWithWallets[formik.values.p2pProviderId],
                      (wallet) => (
                        <li key={wallet.id}>{`${
                          wallet.address
                        } (${wallet.network?.toUpperCase()})`}</li>
                      ),
                    )}
                  </ul>
                </Alert>
              )}
          </FormControls>
        </Dialog>
      )}
    </Formik>
  );
};
